import type { Router } from 'vue-router';
import NProgress from 'nprogress'; // progress bar
import { useUserStore } from '@/store';
import {
  isLogin,
  loginWithSSOCode,
  toSSO,
  clearPrimaryDomainToken,
  getAccessToken,
  clearToken,
} from '@/utils/auth';
import { getUserInfoBySSOTokenApi, initializeUserApi } from '@/api/user';
import { trackEvent, getUTMParams } from '@/utils/analytics';
import { iframeMessageManager } from '@/utils/iframeMessage';
import { LOGIN_WITHOUT_WORKSPACE_LIST, WHITE_LIST } from '../constants';

/**
 * @msg: 用户登录信息路由守卫
 * @param {Router} router
 */
export default function setupUserLoginInfoGuard(router: Router) {
  router.beforeEach(async (to, from, next) => {
    NProgress.start();
    if (to.query.sso_code) {
      try {
        await loginWithSSOCode(to.query.sso_code as string);
        // 移除 URL 中的 sso_code 参数
        const { sso_code, ...restQuery } = to.query;
        const redirectPath = to.path === '/' ? '/discover/homepage' : to.path;
        next({
          path: redirectPath,
          query: restQuery,
          replace: true,
        });
        return;
      } catch {
        // 移除 URL 中的 sso_code 参数
        const url = new URL(window.location.href);
        // 删除指定参数
        url.searchParams.delete('sso_code');
        // 更新地址栏（不刷新）
        window.history.replaceState({}, '', url.toString());
        toSSO();
        return;
      }
    }
    // 优先处理 URL 中的 sso_code 参数
    // if (WHITE_LIST.includes(to.name as string)) {
    //   next();
    //   return;
    // }
    clearPrimaryDomainToken();
    const userStore = useUserStore();

    if (getAccessToken() && !userStore.user_uuid) {
      try {
        const res = await getUserInfoBySSOTokenApi();
        // console.log('res', res);
        let user = {};
        if (res.code === 0) {
          user = res.data.user;
        }
        if (res.code === 20001) {
          const { data: userData } = await initializeUserApi();
          user = userData.user;
          trackEvent('sign_up_complete', { method: 'sso', ...getUTMParams() });
        }
        // 更新 store 中的用户信息
        userStore.setInfo({
          ...user,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('初始化用户信息失败:', error);
        return;
      }
    }
    // 白名单路由不需要 workspace_uuid，但匹配 workspace 路由的需要继续处理
    const pathWithSlash = to.path.startsWith('/') ? to.path : `/${to.path}`;
    const testPath = `/space/_${pathWithSlash}`;
    const isWorkspaceRoute =
      to.matched.some((record) => record.path === '/space/:workspace_uuid') ||
      (to.name === 'notFound' &&
        router
          .resolve(testPath)
          .matched.some((r) => r.path === '/space/:workspace_uuid'));
    if (
      (WHITE_LIST.includes(to.name as string) ||
        LOGIN_WITHOUT_WORKSPACE_LIST.includes(to.name as string)) &&
      !isWorkspaceRoute
    ) {
      next();
      return;
    }

    if (isLogin()) {
      // 确保有 workspace 列表
      if (!userStore.workspaceList.length) {
        await userStore.fetchUserWorkspaceList();
      }

      // 从 URL 中提取 workspace_uuid
      const workspaceUuid = to.params.workspace_uuid as string;

      // 如果路由包含 workspace_uuid，设置到 store
      if (workspaceUuid && userStore.workspaceList.length > 0) {
        const workspace = userStore.workspaceList.find(
          (w) => w.workspace_uuid === workspaceUuid
        );
        if (workspace) {
          userStore.setWorkspace(
            workspace.tenant_uuid,
            workspace.workspace_uuid
          );
        }
      }

      // 如果 URL 中没有 workspace_uuid，但有默认项目，重定向到带 workspace_uuid 的 URL
      if (!workspaceUuid && userStore.workspaceUuid && to.path !== '/login') {
        const cleanPath = to.path.replace(/^\/space\/[^/]+/, '') || '/';
        const suffix = cleanPath === '/' ? '/discover/homepage' : cleanPath;
        next({
          path: `/space/${userStore.workspaceUuid}${suffix}`,
          query: to.query,
          replace: true,
        });
        return;
      }

      // 如果没有 workspace_uuid 且没有默认项目，使用第一个 workspace
      if (
        !workspaceUuid &&
        userStore.workspaceList.length > 0 &&
        !userStore.workspaceUuid
      ) {
        const firstWorkspace = userStore.workspaceList[0];
        userStore.setWorkspace(
          firstWorkspace.tenant_uuid,
          firstWorkspace.workspace_uuid
        );
        const cleanPath = to.path.replace(/^\/space\/[^/]+/, '') || '/';
        const suffix = cleanPath === '/' ? '/discover/homepage' : cleanPath;
        next({
          path: `/space/${firstWorkspace.workspace_uuid}${suffix}`,
          query: to.query,
          replace: true,
        });
        return;
      }

      next();
    } else if (import.meta.env.VITE_LOGIN_TYPE === 'internal') {
      next({ path: '/login' });
    } else {
      // 等待 iframe 加载完成
      const iframeLoaded = await iframeMessageManager.waitForIframeLoad(5000);
      if (!iframeLoaded) {
        console.warn('等待 iframe 加载超时');
        toSSO();
        return;
      }

      // iframe 加载完成后发送消息
      const sendMessageToIframe = async () => {
        const iframe = document.getElementById('sso-iframe');
        if (!iframe) {
          return false;
        }

        // 确保 contentWindow 可用
        if (!iframe.contentWindow) {
          return false;
        }

        try {
          iframe.contentWindow.postMessage(
            { key: 'get_code', value: 'true' },
            import.meta.env.VITE_SSO_URL
          );
          return true;
        } catch (error) {
          console.error('发送消息到 iframe 失败:', error);
          return false;
        }
      };

      const messageSent = await sendMessageToIframe();
      if (!messageSent) {
        // 如果无法发送消息，直接跳转到 SSO
        toSSO();
        return;
      }

      // 检查 iframe 消息中的 sso_code（静默登录）
      const ssoCodeMessage = await iframeMessageManager.waitForMessage(
        'sso_code',
        1000
      );
      if (ssoCodeMessage && ssoCodeMessage.value) {
        try {
          await loginWithSSOCode(ssoCodeMessage.value);
          // 清除已处理的消息
          iframeMessageManager.clearMessage('sso_code');
          // 继续导航，不改变 URL
          next();
        } catch (error) {
          console.error('SSO 登录失败:', error);
          toSSO();
        }
      }
      toSSO();
    }
  });
}
